import React, { FC, useEffect } from "react";
import { Trans, t } from "@helpers/translate";
import { GetStaticPaths, GetStaticProps } from "next";
import { graphql } from "@helpers/graphql";
import {
  Country,
  Supplier,
  SupplierCardFragment,
  TourCardFragment,
  UserOrSupplier,
} from "@graphql/types";
import Link from "next/link";
import { getMetaDataDescription } from "@helpers/getMetaDataDescription";
import { getDateFormat } from "@helpers/getDateFormat";
import { PostCardFragment } from "@graphql/types";
import { getReadingTime } from "@helpers/getReadingTime";
import { getNodes } from "@helpers/getNodes";
import { searchURL } from "@helpers/searchURL";
import { Avatar } from "@components/ui/Avatar";
import { Rating } from "@components/ui/Rating";
import { ButtonLink } from "@components/ui/Button";
import LoadingPage from "@components/pages/LoadingPage";
import { Breadcrumb, BreadcrumbItem } from "@components/ui/Breadcrumb";
import Head from "@components/common/Head";
import { Title } from "@components/ui/Title";

import DidYouLikeMyArticleAd from "@components/common/Ads/DidYouLikeMyArticleAd";
import PlanYourTripAd from "@components/common/Ads/PlanYourTripAd";
import SuppliersBlock from "@components/common/SupplierBlock";
import ToursBlock from "@components/common/ToursBlock";
import PostsBlock from "@components/common/PostsBlock";
import {
  IPostByIdProps,
  loader_PostBySlug,
} from "@components/pages/Blogs/loader";
import { useRouter } from "next/router";
import { PreviewTag } from "@components/common/PreviewTag";
import parse, { DOMNode, Element } from "html-react-parser";
import EnlargeableImage from "@components/ui/EnlargeableImage/EnlargeableImage";
import { Image } from "@components/ui/Image";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { SEOCityLinks } from "@components/common/SEO/SEOCityLinksOld";
import { SEOCountryLinks } from "@components/common/SEO/SEOCountryLinksOld";
import { ROUTES } from "@root/routes";
import BreadcrumbMicroFormat from "@components/common/MicroFormat/BreadcrumbMicroFormat";
import BlogSidebar from "@components/pages/Blogs/BlogSidebar";
import SocialPlatformCard from "@components/common/SocialPlatformCard";
import BlogPlanYourTripAd from "@components/pages/Blogs/BlogPlanYourTripAd";
import { PostCarousel } from "@components/common/Carousel";

const User: FC<{
  user: SupplierCardFragment;
  className?: string;
}> = ({ user, className = "" }) => {
  const utm = "?utm_medium=post&utm_source=profile";

  return (
    <div
      className={`flex flex-wrap md:flex-nowrap items-center border p-4 rounded shadow-lg ${className}`}
    >
      <div className="w-full sm:w-1/2 flex items-center">
        <Avatar
          key={user.id}
          src={user.picture?.url}
          alt={user.displayName}
          size="md"
        />
        <div className="pl-4">
          <h2 className="text-xl font-semibold text-blue_text">
            <Link
              href={user.url}
              title={t({
                id: "common.link.title",
                message: "GoWithGuide - Private Tours & Local Tour Guides",
              })}
            >
              {user.displayName}
            </Link>
          </h2>

          {user?.stats &&
            user?.stats.rating > 0 &&
            user?.stats.reviewsCount > 0 && (
              <>
                <Rating
                  rate={user?.stats.rating}
                  reviews={user?.stats.reviewsCount}
                  alwaysShowDecimals={true}
                  size="sm"
                  variant="stars"
                />
              </>
            )}
        </div>
      </div>
      <div className="w-full sm:w-1/2 pt-6 sm:pt-0 text-right">
        <ButtonLink
          href={`${user.url}?${utm}`}
          title={t({
            id: "blog.block.suppliers.gowithguide.contact",
            message: "GoWithGuide - Contact",
          })}
          variant="secondary"
          size="md"
          className="w-full sm:w-auto"
        >
          Contact {user.displayName}
        </ButtonLink>
      </div>
    </div>
  );
};

const Author: FC<{ user: UserOrSupplier }> = ({ user }) => {
  const isCompanyBlog = user.isAdmin || (!user?.isSupplier && user.isBlogger);

  if (isCompanyBlog) {
    return (
      <div className="mt-4">
        <div className="w-full sm:w-1/2 flex items-center my-2">
          <Avatar
            key={user.id}
            src={user.picture?.url}
            alt={user.displayName}
            size="md"
          />
          <div className="pl-4">
            <h2 className="sm:whitespace-nowrap">
              by{` `}
              <span className="font-bold italic">
                GoWithGuide travel specialist:
              </span>
              <Link
                href={ROUTES.author(user.id)}
                title={t({
                  id: "common.link.title",
                  message: "GoWithGuide - Private Tours & Local Tour Guides",
                })}
                className="ml-2 font-semibold text-blue_text"
              >
                {user.displayName}
              </Link>
            </h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="w-full sm:w-1/2 flex items-center my-2">
        <Avatar
          key={user.id}
          src={user.picture?.url}
          alt={user.displayName}
          size="md"
        />
        <div className="pl-4">
          <h2 className="">
            by{` `}
            <Link
              href={user.url}
              title={t({
                id: "common.link.title",
                message: "GoWithGuide - Private Tours & Local Tour Guides",
              })}
              className="font-semibold text-blue_text"
            >
              {user.displayName}
            </Link>
          </h2>

          {user?.isSupplier &&
            (user as Supplier)?.stats.rating > 0 &&
            (user as Supplier)?.stats.reviewsCount > 0 && (
              <>
                <Rating
                  rate={(user as Supplier)?.stats.rating}
                  reviews={(user as Supplier)?.stats.reviewsCount}
                  alwaysShowDecimals={true}
                  size="xs"
                  variant="default"
                />
              </>
            )}
        </div>
      </div>
    </div>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const postSlug = String(params?.slug);
  const res = await loader_PostBySlug({ slug: postSlug });

  if ("props" in res) {
    // For better seo, add redirect to proper slug url in case some unwanted params in the blog slug.
    // http://localhost:3000/blog/enjoying-the-worlds-natural-beauty-with-a-private-guide-4818 -
    // here we use only 4818 and ignore the actual slug name.But for seo its important
    // TO make sure our seo is not effected , always redirect to our actual url even if there is spelling mistake in the slug
    const post = res["props"].initialData?.post;
    if (post?.postById?.url != `/blog/${postSlug}`) {
      return {
        redirect: {
          destination: post?.postById?.url ? post.postById?.url : "/",
          permanent: true,
        },
      };
    }
  }

  return res;
};

const Page: FC<IPostByIdProps> = ({ initialData }) => {
  if (!initialData) {
    return <LoadingPage />;
  }

  const router = useRouter();
  const post = initialData.post?.postById;
  const relatedPosts = initialData.relatedPosts.RelatedPosts.post;
  const user = post?.user as UserOrSupplier;
  const isCompanyBlog = user?.isAdmin || (!user?.isSupplier && user?.isBlogger);

  useEffect(() => {
    if (post) {
      graphql.incrementPostViews({ id: post.id });
    }
  }, [post]);
  function getBreadCrumb() {
    const items: BreadcrumbItem[] = [];

    if (post) {
      items.push({ text: "Top", href: ROUTES.top() });

      if (post.country) {
        items.push({
          text: post.country.name,
          href: ROUTES.country(post.country.slug).index(),
        });

        if (!post.city) {
          items.push({
            text: "Blog",
            href: ROUTES.country(post.country.slug).blog(),
          });
        }
      }

      const singaporeCityId = 4386;
      const maltaCityId = 4530;

      if (
        post.city &&
        post.country &&
        ![singaporeCityId, maltaCityId].includes(post.city?.id)
      ) {
        items.push({
          text: post.city.name,
          href: ROUTES.country(post.country.slug).city(post.city.slug).index(),
        });

        items.push({
          text: "Blog",
          href: ROUTES.country(post.country.slug).city(post.city.slug).blog(),
        });
      }

      if (!post.country) {
        items.push({ text: "Blog", href: ROUTES.blog.index() });
      }

      items.push({ text: "Post", href: post.url });
    }
    return items;
  }

  const isPreviewPage = router.pathname.endsWith("/preview");

  // Helper function to check if any parent node is an <a> tag
  const isParentLink = (node: Element | null): boolean => {
    while (node) {
      if (node.tagName === "a") {
        return true;
      }
      node = node.parentNode as Element | null;
    }
    return false;
  };

  const options = {
    replace: (domNode: DOMNode) => {
      if (domNode instanceof Element) {
        const props = { ...domNode.attribs };

        // Convert the inline style attribute to a React-compatible style object if it exists
        let styleObject;
        if (props.style) {
          styleObject = props.style.split(";").reduce(
            (styleObj, style) => {
              const [property, value] = style.split(":");
              if (property && value) {
                const camelCaseProperty = property
                  .trim()
                  .replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                styleObj[camelCaseProperty] = value.trim();
              }
              return styleObj;
            },
            {} as Record<string, string>
          );

          // Remove style from props to avoid type conflict
          delete props.style;
        }

        if (isParentLink(domNode)) {
          return;
        }

        // For <img> tags, return the custom EnlargeableImage component
        if (domNode.tagName === "img") {
          return (
            <EnlargeableImage
              {...props}
              style={styleObject} // Apply styleObject here
              alt={props.alt || initialData.post.postById?.title || ""}
            />
          );
        }

        // For other elements, return a generic wrapper with the parsed style applied
      }
      return domNode;
    },
  };

  return (
    <div className="flex w-full lg:mb-32">
      <Head
        title={post?.title || ""}
        description={getMetaDataDescription(post?.overview || "")}
        openGraph={{
          images: [{ url: post?.picture?.url || "" }],
        }}
      />

      {/*
        BlogSidebar component:
        - Mobile/Tablet (below lg: 1024px): Renders as a fixed bottom bar (mobile sidebar) with a table of contents (TOC) and share modal.
        - Desktop (lg: 1024px and above): Renders as a sticky right column on the left side of the layout.
        This component handles its own responsive behavior internally.
      */}
      <BlogSidebar
        post={{
          title: post?.title || "",
          country: post?.country?.name || "",
          url: String(process.env.NEXT_PUBLIC_DOMAIN) + (post?.url || ""),
        }}
        items={initialData.contentHeaders}
      />

      {/*
        Blog All Content for all screen size: Main content wrapper for centering
        - Mobile/Tablet (<lg: 1024px): w-full for full width, justify-center to center content.
        - Desktop (lg: ≥1024px): lg:w-auto for natural width, stretches between left sidebar and right CTA.
      */}
      <div
        className={`flex justify-center w-full lg:w-auto ${isCompanyBlog && relatedPosts && relatedPosts.length > 0 ? "px-4 md:px-0" : ""}`}
      >
        <div className="w-full md:max-w-2xl mx-4" title="blog content">
          <div>
            {isPreviewPage && post && post?.isActive == false && <PreviewTag />}
            <div className="py-5 relative">
              <div className="flex overflow-x-auto w-full items-center">
                <Breadcrumb items={getBreadCrumb()} noWrap />
              </div>
              <BreadcrumbMicroFormat items={getBreadCrumb()} />
              <Title
                size="lg"
                className="mb-0 mt-10"
                style={{ lineHeight: "130%" }}
              >
                {post?.title}
              </Title>
              {user && <Author user={user} />}
              {post?.contentUpdatedAt && (
                <div className="text-gray-500 text-sm">
                  <Trans>
                    Last updated :{" "}
                    {getDateFormat(post?.contentUpdatedAt, "MMM dd, yyyy")}
                  </Trans>
                  <span className="mx-2">{"・"}</span>
                  <span>{getReadingTime(post?.content || "")}</span>
                </div>
              )}

              <div className="flex my-3 flex-col">
                {post?.category && (
                  <div className="flex flex-row items-center">
                    <p className="my-2 mr-4 h-6 border border-gray-200 bg-white text-gwg_dull_gray rounded-2xl px-3 py-1 text-xs">
                      <Link
                        href={ROUTES.blog.category(post.category.slug)}
                        title={t({
                          id: "common.link.title",
                          message:
                            "GoWithGuide - Private Tours & Local Tour Guides",
                        })}
                        className="hover:underline text-gray-600"
                      >
                        {post.category.name}
                      </Link>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="py-4">
            <div>
              <div id="blog-content" className="blog-content">
                {parse(post?.content || "", options)}
              </div>

              {!isCompanyBlog && user && <DidYouLikeMyArticleAd user={user} />}
            </div>

            {!user?.isSupplier && user?.isBlogger && (
              <Link href={getAbsoluteUrl(ROUTES.author(user.id))}>
                <div className="flex flex-col p-8 pt-6 mt-16 shadow-lg-simple rounded-2xl cursor-pointer">
                  <div>
                    <Title as="h2" size="md" divider className="mb-4">
                      <Trans id="blog.writter-card.title">
                        {`Written by ${user.displayName}`}
                      </Trans>
                    </Title>
                  </div>
                  <div className="flex flex-col md:flex-row">
                    {user.picture?.url && (
                      <div className="flex items-center justify-center mb-4 md:justify-start md:mb-0">
                        <div className="flex w-32 h-32 relative mr-8">
                          <Image
                            key={user.id}
                            src={user.picture?.url}
                            alt={"blog writer avatar"}
                            activeCache={false}
                            className="rounded-full"
                            fill={true}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex w-full">
                      <h3 className="truncate-5 text-clip self-center">
                        {user.biography ||
                          "This profile is used for our old blog posts"}
                      </h3>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {!isCompanyBlog && post?.user && (
              <div className="mt-16">
                <User user={post.user as SupplierCardFragment} className="" />

                <PlanYourTripAd
                  city={post?.city}
                  user={user}
                  className="mt-16"
                  country={post?.country as Country}
                />
              </div>
            )}

            {isCompanyBlog ? (
              <SuppliersBlock
                title={t({
                  id: "blog.block.popular-guides.title.generic",
                  message: `Popular ${
                    post?.city?.name || post?.country?.name || ""
                  } Tour Guides`,
                })}
                data={getNodes(initialData.suppliers?.suppliers)}
                columns="3"
                className="mt-16"
              >
                {!post?.country ? (
                  <ButtonLink
                    variant="default-outline"
                    href={searchURL({
                      t: "guides",
                      country: post?.city?.slug
                        ? undefined
                        : post?.country?.slug,
                      city: post?.city?.slug,
                    })}
                    title={t({
                      id: "blog.block.suppliers.gowithguide.view.tour.guides.",
                      message: "GoWithGuide - View All Tour Guides",
                    })}
                    className=" max-w-xs sm:max-w-sm md:max-w-full truncate"
                  >
                    <Trans id="blog.block.suppliers.button.generic">
                      View All Tour Guides
                    </Trans>
                  </ButtonLink>
                ) : (
                  initialData?.suppliers?.suppliers.pageInfo.hasNextPage && (
                    <ButtonLink
                      variant="default-outline"
                      href={searchURL({
                        t: "guides",
                        country: post?.city?.slug
                          ? undefined
                          : post?.country?.slug,
                        city: post?.city?.slug,
                      })}
                      title={t({
                        id: "blog.block.suppliers.gowithguide.view.tour.guides.",
                        message: "GoWithGuide - View All Tour Guides",
                      })}
                      className=" max-w-xs sm:max-w-sm md:max-w-full truncate"
                    >
                      <Trans id="blog.block.suppliers.button.generic">
                        View All {post?.city?.name || post?.country?.name || ""}{" "}
                        Tour Guides
                      </Trans>
                    </ButtonLink>
                  )
                )}
              </SuppliersBlock>
            ) : (
              <div className="mt-16">
                {/* Recommended Suppliers */}
                <ToursBlock
                  title={
                    isCompanyBlog
                      ? t({
                          id: "post.block.recommended-tours.title",
                          message: "Recommended Tours",
                        })
                      : t({
                          id: "post.block.recommended-tours.by-guide",
                          message: `Tours By ${user.displayName}`,
                        })
                  }
                  columns="3"
                  showUser={true}
                  data={
                    initialData.tours.RelatedToursByPost
                      .tours as TourCardFragment[]
                  }
                />
              </div>
            )}
            {isCompanyBlog && (post?.city || post?.country) && (
              <PlanYourTripAd
                city={post?.city}
                className="mt-16"
                country={post?.country as Country}
              />
            )}

            {isCompanyBlog && !post?.country && (
              <PlanYourTripAd className="mt-16" user={user} />
            )}

            {isCompanyBlog ? (
              relatedPosts &&
              relatedPosts.length > 0 && (
                <div className="mt-16">
                  <PostCarousel
                    title={"Related Blogs"}
                    posts={relatedPosts || []}
                    needMoreLink={
                      post?.city?.url
                        ? `${post?.city?.url}/blog`
                        : post?.country?.url
                          ? `${post?.country?.url}/blog`
                          : "/blog"
                    }
                  />
                </div>
              )
            ) : (
              <>
                {/* Related blogs section for desktop (md: 768px and above) */}
                {relatedPosts && (
                  <div className="mt-16 hidden md:flex">
                    <PostsBlock
                      orientation="horizontal"
                      title={t({
                        id: "post.block.related-posts.title",
                        message: "Related Blogs",
                      })}
                      rows="3"
                      showUser={true}
                      data={relatedPosts as PostCardFragment[]}
                      className="mb-8"
                    />
                  </div>
                )}

                {/* Related blogs section for mobile (below md: 768px) */}
                {relatedPosts && (
                  <div className="mt-16 flex md:hidden">
                    <PostsBlock
                      orientation="vertical"
                      title={t({
                        id: "post.block.related-posts.title",
                        message: "Related Blogs",
                      })}
                      columns="3"
                      showUser={true}
                      data={relatedPosts as PostCardFragment[]}
                      className=""
                    />
                  </div>
                )}
              </>
            )}

            <div>
              {initialData.footerCity && (
                <SEOCityLinks {...initialData.footerCity} />
              )}
              {initialData.footerCountry && (
                <SEOCountryLinks {...initialData.footerCountry} />
              )}
            </div>
            <div className="my-16">
              <SocialPlatformCard />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop right sidebar: CTA Button */}
      <div className="w-1/4 mr-auto hidden lg:flex pr-4">
        <BlogPlanYourTripAd
          post={{
            title: post?.title || "",
            city: post?.city,
            country: post?.country,
          }}
        />
      </div>
    </div>
  );
};

export default Page;
